import { useState } from "react"
import { Info, Spinner } from "@phosphor-icons/react"
import { fetchQuery, graphql } from "relay-runtime"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { formatDate } from "@/lib/format-date"
import {
  medicationNewsTooltipQuery,
  medicationNewsTooltipQuery$data,
  medicationNewsTooltipQuery$variables,
} from "@/modules/patients/detail/medication/ch/__generated__/medicationNewsTooltipQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"

const chMedicationNewsQuery = graphql`
  query medicationNewsTooltipQuery($id: Int!) {
    ch_medicationNews(itemId: $id) {
      leadText
      publicationDate
      subjectArea {
        description
        id
      }
      title
    }
  }
`

export const medicationNewsFetch = (input: medicationNewsTooltipQuery$variables["id"]) => {
  return fetchQuery<medicationNewsTooltipQuery>(
    RelayEnvironment,
    chMedicationNewsQuery,
    { id: input },
    { fetchPolicy: "store-or-network" },
  ).toPromise()
}

export const MedicationNewsTooltip = ({ medicationItemId }: { medicationItemId: number }) => {
  const [chMedicationNews, setChMedicationNews] = useState<medicationNewsTooltipQuery$data>()
  const [isPending, setIsPending] = useState(false)

  const handleChMedicationNewsTooltipFetch = async () => {
    if (chMedicationNews || isPending) return
    setIsPending(true)

    if (medicationItemId) {
      medicationNewsFetch(medicationItemId)
        .then((data) => {
          if (data) setChMedicationNews(data)
        })
        .finally(() => {
          setIsPending(false)
        })
    }
  }

  if (!medicationItemId) return null

  return (
    <TooltipCustom
      onOpenChange={(open) => {
        if (!open) {
          setIsPending(false)
          setChMedicationNews(undefined)
        }
      }}>
      <TooltipTrigger onMouseEnter={handleChMedicationNewsTooltipFetch} onClick={(e) => e.preventDefault()}>
        {isPending ? <Spinner size={24} /> : <Info size={24} />}
      </TooltipTrigger>
      <TooltipPortal>
        {chMedicationNews && (
          <TooltipContent
            sideOffset={7}
            align="end"
            side="bottom"
            className="bg-surface-inverted text-on-surface-inverted px-1 py-0.5 text-xs tracking-[0.25px] wrap-break-word">
            <div className="flex flex-col gap-2">
              {chMedicationNews.ch_medicationNews.map((value) => {
                const items = [
                  formatDate(new Date(value.publicationDate)),
                  value.subjectArea.description,
                  value.title,
                ].filter((item) => item && item.trim() !== "")

                return (
                  <div key={value.title + value.publicationDate}>
                    <p className="font-semibold">
                      {items.map((item, i) => (
                        <span key={item}>
                          <PotentiallyUnsafeInnerHtml value={item} className="inline" />
                          {i < items.length - 1 && <span>{" • "}</span>}
                        </span>
                      ))}
                    </p>
                    <PotentiallyUnsafeInnerHtml value={value.leadText} />
                  </div>
                )
              })}
            </div>
          </TooltipContent>
        )}
      </TooltipPortal>
    </TooltipCustom>
  )
}
